import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Col, Row } from 'reactstrap';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { renderValidationError } from 'utils/util/formatter';
import SwitchInput from 'views/Widgets/Inputs/SwitchInput';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import Loading from 'views/Widgets/Loading';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import FilterablePeopleTable, {
  TALBE_SIZE_NO_LIMIT,
} from 'views/Widgets/People/FilterablePeopleTable';
import Scrollable from 'views/Widgets/Scrollable';
import { useCampaignReports } from '../CampaignReportsContext';
import PeopleEditorOverlay from '../PeopleEditorOverlay';
import {
  ApiCampaignReport,
  ApiCampaignReportItem,
  CampaignReportItemStatus,
  formatCampaignReportDescriptor,
  formatPeopleListForSorting,
  toUpdateItemsApiPayload,
} from './EngagementSurvey';

interface Props {
  reportId: number;
  isOpen: boolean;
  onClosed: (hasPublishedItems: boolean) => void;
  onCallback: (hasPublishedItems: boolean) => void;
}

type ReportData = {
  description: ApiCampaignReport;
  items: ApiCampaignReportItem[];
};

const EngagementSurveyReportEditModalEditor: FC<Props> = ({
  reportId,
  isOpen,
  onClosed,
  onCallback,
}) => {
  const { formatMessage } = useIntl();
  const { campaignId, canPublish } = useCampaignReports();
  const proxyParameters = useProxyParameters();

  const [object, setObject] = useState<ReportData | null>(null);
  const [hasPublishedItems, setHasPublishedItems] = useState(false);

  const onSuccesfullRetrieve = useCallback(
    (
      data: ReportData | null,
      error: unknown,
      hardErrorMessage: unknown
    ): void => {
      if (!error && !hardErrorMessage) {
        setObject(data);
      }
    },
    []
  );

  const { error: errorForReportData } = useConfirmApi<ReportData>({
    url: `/campaigns/${campaignId}/reports/${reportId}`,
    method: 'GET',
    clearOnChange: true,
    clearOnError: true,
    disabled: !isOpen,
    callback: onSuccesfullRetrieve,
  });

  const handleInputsChange = useCallback((_newObject) => {
    // Do nothing
  }, []);

  const handleOnClosed = useCallback(() => {
    onClosed(hasPublishedItems);
    setObject(null);
  }, [onClosed, hasPublishedItems]);

  const handleOnValueItemChanged = useCallback(
    (id, reportId) => (value) => {
      setObject((object) => ({
        ...object!,
        items: object!.items.map((item) => {
          if (item.id === reportId) {
            return { ...item, [id]: value };
          }
          return item;
        }),
      }));
    },
    []
  );

  const handleCallback = useCallback(() => {
    onCallback(hasPublishedItems);
  }, [onCallback, hasPublishedItems]);

  const handleOnSubmit = useCallback(
    (submittedObject, onSubmit) => {
      ConfirmAPI.sendRequestToConfirm(
        'POST',
        `/campaigns/${campaignId}/reports/${reportId}/update`,
        toUpdateItemsApiPayload(submittedObject, proxyParameters),
        (data, error: unknown, hardErrorMessage: unknown) => {
          onSubmit(data, error, hardErrorMessage);
          // Check if we have any published items after successful save
          if (!error && !hardErrorMessage && data) {
            // If we have data and items, check if any are published
            const hasPublishedItems = data.items?.some(
              (item) => item.status === CampaignReportItemStatus.PUBLISHED
            );
            setHasPublishedItems(!!hasPublishedItems);
            onCallback(!!hasPublishedItems);
          }
        }
      );
    },
    [campaignId, proxyParameters, reportId, onCallback]
  );

  const renderForm = useCallback(
    (inputs, submitButton) => {
      if (!object) {
        return (
          <Loading
            message={formatMessage({
              id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.message.loading_report_details',
              defaultMessage: 'Loading report details...',
            })}
          />
        );
      }
      const reportInfo = formatCampaignReportDescriptor(
        object!.description,
        formatMessage
      );
      return (
        <>
          <Row>
            <Col>
              <h4>{reportInfo.title}</h4>
              <small className="text-muted">{reportInfo.description}</small>
            </Col>
          </Row>
          <Row className="mt-4">{inputs}</Row>
          {errorForReportData && (
            <Row>{renderValidationError(errorForReportData)}</Row>
          )}
          <Row className="mt-4">
            <Col>
              <Button className="w-100" color="light" onClick={handleOnClosed}>
                <FormattedMessage
                  id="app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.cancel"
                  defaultMessage="Cancel"
                />
              </Button>
            </Col>
            <Col>{submitButton}</Col>
          </Row>
        </>
      );
    },
    [errorForReportData, formatMessage, handleOnClosed, object]
  );

  const rows = useMemo(() => {
    const getPublishedStatusIsDisabled = (item) => {
      return (
        item.status === CampaignReportItemStatus.UNPUBLISHED &&
        item.viewers.length === 0 &&
        item.owners.length === 0
      );
    };

    return (
      object?.items.map((item) => {
        return {
          // @ts-expect-error
          readOnly: !item.reach_minimum_threshold,
          id: item.id,
          slice: item.name,
          owners: (
            <PeopleEditorOverlay
              selectedPeople={item.owners}
              editTitle={'Edit people that will own the report'}
              onUpdate={handleOnValueItemChanged('owners', item.id)}
              readonly={false}
            />
          ),
          owners_sort: formatPeopleListForSorting('owners', 'email'),
          viewers: (
            <PeopleEditorOverlay
              selectedPeople={item.viewers}
              editTitle={'Edit people that will view the report'}
              onUpdate={handleOnValueItemChanged('viewers', item.id)}
              readonly={false}
            />
          ),
          viewers_sort: formatPeopleListForSorting('viewers', 'email'),
          count: item.count,
          // Do a dropdown for status
          status: (
            <div>
              <SwitchInput
                name="status"
                centered={true}
                value={item.status}
                checkedValue={CampaignReportItemStatus.PUBLISHED}
                uncheckedValue={CampaignReportItemStatus.UNPUBLISHED}
                onChange={handleOnValueItemChanged('status', item.id)}
                disabled={!canPublish || getPublishedStatusIsDisabled(item)}
                enableHelp={!canPublish || getPublishedStatusIsDisabled(item)}
                hoverHelperId={`status-${item.id}`}
                hoverHelperText={
                  getPublishedStatusIsDisabled(item)
                    ? formatMessage({
                        id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.table.column.status.hover_helper_text',
                        defaultMessage:
                          'Please add viewers or owners to the report to be able to publish it.',
                      })
                    : formatMessage({
                        id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.table.column.status.helper_text_phase_date',
                        defaultMessage:
                          'You will be able to publish the report once the survey has ended.',
                      })
                }
              />
            </div>
          ),
          status_sort: item.status,
        };
      }) || []
    );
  }, [formatMessage, handleOnValueItemChanged, object?.items, canPublish]);

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.table.column.slice.name',
          defaultMessage: 'Slice',
        }),
        field: 'slice',
      },
      {
        name: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.table.column.count.name',
          defaultMessage: '# People',
        }),
        field: 'count',
      },
      {
        name: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.table.column.owners.name',
          defaultMessage: 'Owners',
        }),
        field: 'owners',
        sort: 'owners_sort',
      },
      {
        name: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.table.column.viewers.name',
          defaultMessage: 'Viewers',
        }),

        field: 'viewers',
        sort: 'viewers_sort',
      },
      {
        name: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.table.column.published.name',
          defaultMessage: 'Published',
        }),
        field: 'status',
        sort: 'status_sort',
      },
    ],
    [formatMessage]
  );

  const inputs = useMemo(
    () => [
      {
        type: INPUT_TYPES.CUSTOM_INPUT,
        component: (
          <Scrollable maxHeight="800px">
            <FilterablePeopleTable
              className="mb-0"
              rows={rows}
              columns={columns}
              hideFilters={true}
              hideExportButton={true}
              initialTableSize={TALBE_SIZE_NO_LIMIT}
            />
          </Scrollable>
        ),
      },
    ],
    [columns, rows]
  );

  const handleOnValidate = useCallback(
    (obj) => {
      // Ensure all published items have owners and viewers.
      const publishedItems = obj.items.filter(
        (item) => item.status === CampaignReportItemStatus.PUBLISHED
      );
      const errors = [];
      publishedItems.forEach((item) => {
        if (item.owners.length === 0 && item.viewers.length === 0) {
          errors['Error'] = formatMessage({
            id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.error.published_items_must_have_owners_and_viewers',
            defaultMessage:
              'Published reports must have at least either an owner or viewer.',
          });
        }
      });
      return errors;
    },
    [formatMessage]
  );

  return (
    <ModalEditor
      title={formatMessage({
        id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.title.edit_report',
        defaultMessage: 'Edit report',
      })}
      isOpen={isOpen}
      toggle={handleOnClosed}
      object={object}
      inputs={inputs}
      onInputsChange={handleInputsChange}
      callback={handleCallback}
      renderForm={renderForm}
      submitPromise={handleOnSubmit}
      submitText={formatMessage({
        id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.submitText.save',
        defaultMessage: 'Save',
      })}
      buttonClassName=""
      onClosed={handleOnClosed}
      className="mb-0"
      onValidate={handleOnValidate}
    />
  );
};

export default React.memo(EngagementSurveyReportEditModalEditor);
